<template>
  <div class="home">
    <!-- Hero Section -->
    <section class="hero bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-700 text-white py-20" style="margin: -24px -24px 24px -24px; border-radius: 8px 8px 0 0;">
      <div class="absolute inset-0 bg-black opacity-20"></div>
      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div class="text-center">
          <h1 class="text-4xl md:text-6xl font-bold mb-6 animate-fade-in">
            Welcome to Vue 3 + Vite
          </h1>
          <p class="text-xl md:text-2xl mb-8 max-w-3xl mx-auto opacity-90 animate-fade-in-delay-1">
            Experience the power of modern web development with Vue 3, Vite, and the latest frontend technologies.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-delay-2">
            <router-link
              to="/dashboard"
              class="px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-lg"
            >
              Get Started
            </router-link>
            <router-link
              to="/about"
              class="px-8 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-600 transform hover:scale-105 transition-all duration-300"
            >
              Learn More
            </router-link>
          </div>
        </div>
      </div>
      
      <!-- Animated background elements -->
      <div class="absolute top-20 left-10 w-20 h-20 bg-white opacity-10 rounded-full animate-bounce"></div>
      <div class="absolute bottom-20 right-10 w-32 h-32 bg-white opacity-10 rounded-full animate-bounce-delay-2"></div>
      <div class="absolute top-40 right-20 w-16 h-16 bg-white opacity-10 rounded-full animate-bounce-delay-4"></div>
    </section>

    <!-- Features Section -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Modern Features
          </h2>
          <p class="text-xl text-gray-600 max-w-2xl mx-auto">
            Built with the latest technologies and best practices for optimal performance and developer experience.
          </p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div
            v-for="(feature, index) in features"
            :key="feature.title"
            class="group relative p-8 bg-white border border-gray-200 rounded-xl hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            :class="`animate-fade-in-delay-${index + 1}`"
          >
            <div class="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
              {{ feature.icon }}
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">
              {{ feature.title }}
            </h3>
            <p class="text-gray-600 leading-relaxed">
              {{ feature.description }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="py-20 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid md:grid-cols-4 gap-8 text-center">
          <div
            v-for="(stat, index) in stats"
            :key="stat.label"
            class="animate-fade-in-delay-1"
          >
            <div class="text-4xl font-bold text-blue-600 mb-2">
              {{ stat.value }}
            </div>
            <div class="text-gray-600">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-3xl md:text-4xl font-bold mb-6">
          Ready to Get Started?
        </h2>
        <p class="text-xl mb-8 max-w-2xl mx-auto opacity-90">
          Join thousands of developers building amazing applications with Vue 3 and Vite.
        </p>
        <router-link
          to="/dashboard"
          class="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-lg"
        >
          Start Building
          <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5-5m5 5H6"/>
          </svg>
        </router-link>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const features = ref([
  {
    icon: '⚡',
    title: 'Lightning Fast',
    description: 'Vite provides instant hot module replacement and optimized builds for the best developer experience.'
  },
  {
    icon: '🎨',
    title: 'Modern UI',
    description: 'Beautiful, responsive design with Tailwind CSS and smooth animations for an engaging user experience.'
  },
  {
    icon: '🔧',
    title: 'Developer Tools',
    description: 'Complete toolchain with ESLint, Prettier, and modern development tools for code quality.'
  },
  {
    icon: '📱',
    title: 'Mobile First',
    description: 'Responsive design that works perfectly on all devices, from mobile phones to desktop computers.'
  },
  {
    icon: '🚀',
    title: 'Performance',
    description: 'Optimized for speed with lazy loading, code splitting, and efficient bundle management.'
  },
  {
    icon: '🛡️',
    title: 'Type Safety',
    description: 'Built with modern JavaScript features and optional TypeScript support for better code reliability.'
  }
])

const stats = ref([
  { value: '3.4+', label: 'Vue Version' },
  { value: '5.1+', label: 'Vite Version' },
  { value: '100%', label: 'Modern ES' },
  { value: '⚡', label: 'Lightning Fast' }
])
</script>

<style scoped>
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.animate-fade-in {
  animation: fade-in 0.6s ease-out forwards;
}

.animate-fade-in-delay-1 {
  animation: fade-in 0.6s ease-out 0.1s forwards;
  opacity: 0;
}

.animate-fade-in-delay-2 {
  animation: fade-in 0.6s ease-out 0.2s forwards;
  opacity: 0;
}

.animate-fade-in-delay-3 {
  animation: fade-in 0.6s ease-out 0.3s forwards;
  opacity: 0;
}

.animate-fade-in-delay-4 {
  animation: fade-in 0.6s ease-out 0.4s forwards;
  opacity: 0;
}

.animate-fade-in-delay-5 {
  animation: fade-in 0.6s ease-out 0.5s forwards;
  opacity: 0;
}

.animate-fade-in-delay-6 {
  animation: fade-in 0.6s ease-out 0.6s forwards;
  opacity: 0;
}

.animate-bounce {
  animation: bounce 2s infinite;
}

.animate-bounce-delay-2 {
  animation: bounce 2s infinite 0.5s;
}

.animate-bounce-delay-4 {
  animation: bounce 2s infinite 1s;
}
</style>
